import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mlm/ui';
import { getTenantFeatures } from '../lib/api';
import { useIsSuperAdmin } from '../lib/auth-store';

const SESSION_FLAG = 'admin.domainExpiryShown';
// Remind within 3 months of expiry (and after it's already passed).
const WARN_DAYS = 90;

/**
 * Renewal reminder shown on login when the tenant's `domain_expires_at` is within
 * 3 months (or already past). Shows once per session (sessionStorage flag).
 * Notification-only — it never blocks access. The super-admin sets the date, so
 * they are not nagged; only the tenant admin sees it. Modeled on the member
 * UnpaidOrdersGate.
 */
export function DomainExpiryGate() {
  const isSuper = useIsSuperAdmin();
  const alreadyShown =
    typeof window !== 'undefined' && window.sessionStorage.getItem(SESSION_FLAG) === '1';
  const [dismissed, setDismissed] = useState(alreadyShown);

  const featuresQ = useQuery({
    queryKey: ['tenant-features'],
    queryFn: getTenantFeatures,
    enabled: !dismissed && !isSuper,
    staleTime: 5 * 60_000,
  });

  if (isSuper || dismissed) return null;
  const expiresAt = featuresQ.data?.domain_expires_at;
  if (!expiresAt) return null;

  const expMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expMs)) return null;
  const days = Math.ceil((expMs - Date.now()) / 86_400_000);
  if (days > WARN_DAYS) return null;

  const expired = days < 0;
  const dateLabel = new Date(expiresAt).toLocaleDateString();

  function close() {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(SESSION_FLAG, '1');
    setDismissed(true);
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Renewal reminder"
    >
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-elevated">
        <h2 className="text-h2">{expired ? 'Subscription expired' : 'Renewal reminder'}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {expired
            ? `Your service expired on ${dateLabel}. Please contact your provider to renew and avoid interruption.`
            : `Your service expires on ${dateLabel} (in ${days} day${days === 1 ? '' : 's'}). Please contact your provider to renew.`}
        </p>
        <div className="mt-5 flex items-center justify-end gap-3">
          <Button onClick={close}>Got it</Button>
        </div>
      </div>
    </div>
  );
}
